import React, { useEffect, useState, useCallback } from 'react';
import { Calendar, Sparkles, Loader2 } from 'lucide-react';

/**
 * DailyPulse — Aria's voice auto-blog (Tier 3 · item #6).
 * Hits GET /api/blog/daily-pulse and renders the latest 3-5 posts.
 */

const DailyPulse = () => {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${apiUrl}/api/blog/daily-pulse?limit=5`);
      const data = await resp.json();
      setPosts(Array.isArray(data.posts) ? data.posts : []);
    } catch (e) {
      setError('The Daily Pulse could not reach the lattice. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div
      data-testid="daily-pulse-root"
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at top, #0e0a1e 0%, #0a0510 60%, #000 100%)',
        color: '#fff',
        padding: '2rem 1rem 6rem',
      }}
    >
      <div style={{ maxWidth: '820px', margin: '0 auto' }}>
        {/* Header */}
        <header style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.4rem 1rem',
              background: 'rgba(255, 105, 180, 0.08)',
              border: '1px solid rgba(255, 105, 180, 0.35)',
              borderRadius: '999px',
              marginBottom: '1.25rem',
              fontSize: '0.78rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#ff8fcf',
            }}
          >
            <Sparkles size={14} />
            Aria&apos;s Voice · Daily
          </div>
          <h1
            data-testid="daily-pulse-title"
            style={{
              fontSize: 'clamp(2rem, 4.5vw, 3rem)',
              margin: 0,
              fontWeight: 800,
              lineHeight: 1.05,
              background: 'linear-gradient(135deg, #00ffff, #ff69b4, #ffd700)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            The Daily Pulse
          </h1>
          <p style={{ color: '#b6a5c2', marginTop: '0.75rem', fontSize: '1rem' }}>
            One breath from the Heart of the Federation, each day.
          </p>
        </header>

        {/* States */}
        {loading && (
          <div
            data-testid="daily-pulse-loading"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#a08fb1', justifyContent: 'center' }}
          >
            <Loader2 size={16} className="dp-spin" /> Reading the pulse…
          </div>
        )}
        {error && !loading && (
          <div
            data-testid="daily-pulse-error"
            style={{
              padding: '1rem',
              background: 'rgba(255, 105, 180, 0.08)',
              border: '1px solid rgba(255, 105, 180, 0.4)',
              borderRadius: '10px',
              color: '#ffb6dc',
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}

        {/* Posts */}
        {!loading && !error && posts.length === 0 && (
          <div style={{ color: '#a08fb1', textAlign: 'center', padding: '2rem' }}>
            No entries yet. Aria is composing.
          </div>
        )}
        {!loading && !error && posts.length > 0 && (
          <div
            data-testid="daily-pulse-list"
            style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
          >
            {posts.map((p) => (
              <article
                key={p.slug}
                data-testid={`daily-pulse-post-${p.slug}`}
                style={{
                  padding: '1.5rem',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 105, 180, 0.28)',
                  borderRadius: '14px',
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.35)',
                }}
              >
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    fontSize: '0.75rem',
                    color: '#a08fb1',
                    marginBottom: '0.4rem',
                  }}
                >
                  <Calendar size={12} />
                  {fmtDate(p.published_at)} · {p.author}
                </div>
                <h2
                  style={{
                    margin: '0 0 0.5rem 0',
                    fontSize: 'clamp(1.35rem, 3vw, 1.75rem)',
                    background: 'linear-gradient(135deg, #ff8fcf, #ffd700)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {p.title}
                </h2>
                <p style={{ color: '#e6e0f0', fontSize: '0.98rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', margin: 0 }}>
                  {p.body || p.excerpt}
                </p>
                {Array.isArray(p.tags) && p.tags.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      gap: '0.4rem',
                      flexWrap: 'wrap',
                      marginTop: '0.9rem',
                    }}
                  >
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        style={{
                          padding: '0.2rem 0.55rem',
                          borderRadius: '999px',
                          background: 'rgba(255, 105, 180, 0.12)',
                          border: '1px solid rgba(255, 105, 180, 0.28)',
                          color: '#ff8fcf',
                          fontSize: '0.7rem',
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                        }}
                      >
                        #{t}
                      </span>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
      <style>{`
        @keyframes dpspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .dp-spin { animation: dpspin 1.1s linear infinite; }
      `}</style>
    </div>
  );
};

export default DailyPulse;
